import { useCallback, useEffect, useMemo, useState } from 'react';
import adminApi from '@/lib/adminApi';
import { useLearnEnabled, LearnPaused } from '@/lib/learnFeature';

const statusOptions = ['', 'draft', 'in_review', 'published', 'archived', 'deleted'];

const safeDiagramSvg = (value) => {
  const svg = String(value || '').trim();
  if (!svg || !svg.startsWith('<svg') || !svg.endsWith('</svg>')) return '';
  if (/<script|javascript:|on\w+=|<image|href=|xlink:href|data:/i.test(svg)) return '';
  return svg;
};

const parseLevelsJson = (value) => {
  try {
    const levels = JSON.parse(value || '[]');
    return Array.isArray(levels) ? levels : [];
  } catch (_error) {
    return [];
  }
};

const adminErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || item?.message || String(item)).join('; ');
  return error?.message || fallback;
};

function DiagramPreview({ svg }) {
  const safeSvg = safeDiagramSvg(svg);
  if (!safeSvg) return null;
  return (
    <div
      className="mt-3 overflow-hidden rounded-2xl border border-emerald-100 bg-white p-3 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-h-72 [&_svg]:max-w-full"
      dangerouslySetInnerHTML={{ __html: safeSvg }}
    />
  );
}

function ActionNotice({ notice }) {
  if (!notice?.text) return null;
  const classes = notice.type === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : notice.type === 'working'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-emerald-200 bg-emerald-50 text-emerald-800';
  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm font-bold ${classes}`}>
      {notice.text}
    </div>
  );
}

const emptyGenerateForm = {
  art_form_id: '',
  assumed_weekly_hours: 5,
  language: 'en',
  source_notes: '',
};

const emptyPlanForm = {
  plan_key: '',
  display_name: '',
  price_inr: 0,
  billing_period: 'monthly',
  submissions_per_month: 0,
  includes_coached_forms: false,
  includes_coach_chat: false,
  is_active: false,
};

export default function AdminLearn() {
  const enabled = useLearnEnabled();
  return enabled ? <AdminLearnContent /> : <LearnPaused />;
}

function AdminLearnContent() {
  const [artForms, setArtForms] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [planSaving, setPlanSaving] = useState('');
  const [planNotice, setPlanNotice] = useState(null);
  const [status, setStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [levelsJson, setLevelsJson] = useState('[]');
  const [generateForm, setGenerateForm] = useState(emptyGenerateForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorAction, setEditorAction] = useState('');
  const [diagramGeneratingId, setDiagramGeneratingId] = useState('');
  const [diagramPreviews, setDiagramPreviews] = useState({});
  const [diagramStatuses, setDiagramStatuses] = useState({});
  const [generateNotice, setGenerateNotice] = useState(null);
  const [queueNotice, setQueueNotice] = useState(null);
  const [editorNotice, setEditorNotice] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [formsResponse, curriculaResponse, plansResponse] = await Promise.all([
        adminApi.get('/learn/art-forms'),
        adminApi.get('/admin/learn/curricula', {
          params: status === 'deleted' ? { deleted: 'true' } : status ? { status } : {},
        }),
        adminApi.get('/admin/learn/subscription-plans'),
      ]);
      const forms = formsResponse.data?.art_forms || [];
      setArtForms(forms);
      setCurricula(curriculaResponse.data?.curricula || []);
      setSubscriptionPlans(plansResponse.data?.plans || []);
      setSelectedIds((prev) => prev.filter((id) => (curriculaResponse.data?.curricula || []).some((item) => item.id === id)));
      setGenerateForm((prev) => ({ ...prev, art_form_id: prev.art_form_id || forms[0]?.id || '' }));
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Could not load Learn admin data.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedArtForm = useMemo(() => artForms.find((form) => form.id === generateForm.art_form_id), [artForms, generateForm.art_form_id]);
  const allVisibleSelected = curricula.length > 0 && curricula.every((curriculum) => selectedIds.includes(curriculum.id));
  const parsedLevels = useMemo(() => parseLevelsJson(levelsJson), [levelsJson]);

  const openCurriculum = (curriculum) => {
    setSelected({ ...curriculum });
    setLevelsJson(JSON.stringify(curriculum.levels || [], null, 2));
    setDiagramGeneratingId('');
    setDiagramPreviews({});
    setDiagramStatuses({});
    setEditorAction('');
    setEditorNotice(null);
    setMessage('');
    setError('');
  };

  const setDiagramStatus = (lessonId, type, text) => {
    if (!lessonId) return;
    setDiagramStatuses((prev) => ({
      ...prev,
      [lessonId]: { type, text },
    }));
  };

  const toggleSelectedId = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? [] : curricula.map((curriculum) => curriculum.id));
  };

  const updatePlanDraft = (planKey, field, value) => {
    setSubscriptionPlans((prev) => prev.map((plan) => (plan.plan_key === planKey ? { ...plan, [field]: value } : plan)));
  };

  const createSubscriptionPlan = async (event) => {
    event.preventDefault();
    setPlanSaving('create');
    setPlanNotice({ type: 'working', text: 'Creating Learn plan configuration...' });
    try {
      await adminApi.post('/admin/learn/subscription-plans', {
        ...planForm,
        price_inr: Number(planForm.price_inr || 0),
        submissions_per_month: Number(planForm.submissions_per_month || 0),
      });
      setPlanForm(emptyPlanForm);
      setPlanNotice({ type: 'success', text: 'Plan configuration created. No payment or checkout was enabled.' });
      await loadData();
    } catch (planError) {
      setPlanNotice({ type: 'error', text: adminErrorMessage(planError, 'Could not create Learn plan configuration.') });
    } finally {
      setPlanSaving('');
    }
  };

  const saveSubscriptionPlan = async (plan) => {
    setPlanSaving(plan.plan_key);
    setPlanNotice({ type: 'working', text: `Saving ${plan.display_name || plan.plan_key}...` });
    try {
      await adminApi.patch(`/admin/learn/subscription-plans/${encodeURIComponent(plan.plan_key)}`, {
        display_name: plan.display_name,
        price_inr: Number(plan.price_inr || 0),
        billing_period: plan.billing_period,
        submissions_per_month: Number(plan.submissions_per_month || 0),
        includes_coached_forms: Boolean(plan.includes_coached_forms),
        includes_coach_chat: Boolean(plan.includes_coach_chat),
        is_active: Boolean(plan.is_active),
      });
      setPlanNotice({ type: 'success', text: 'Plan configuration saved. No payment or checkout was enabled.' });
      await loadData();
    } catch (planError) {
      setPlanNotice({ type: 'error', text: adminErrorMessage(planError, 'Could not save Learn plan configuration.') });
    } finally {
      setPlanSaving('');
    }
  };

  const setSubscriptionPlanActive = async (plan, active) => {
    setPlanSaving(plan.plan_key);
    setPlanNotice({ type: 'working', text: `${active ? 'Activating' : 'Deactivating'} ${plan.display_name || plan.plan_key}...` });
    try {
      await adminApi.post(`/admin/learn/subscription-plans/${encodeURIComponent(plan.plan_key)}/${active ? 'activate' : 'deactivate'}`);
      setPlanNotice({ type: 'success', text: `Plan ${active ? 'activated' : 'deactivated'} as configuration only.` });
      await loadData();
    } catch (planError) {
      setPlanNotice({ type: 'error', text: adminErrorMessage(planError, `Could not ${active ? 'activate' : 'deactivate'} Learn plan.`) });
    } finally {
      setPlanSaving('');
    }
  };

  const generateCurriculum = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    setGenerateNotice({ type: 'working', text: 'Generating curriculum draft...' });
    try {
      const response = await adminApi.post('/learn/curricula/generate', {
        art_form_id: generateForm.art_form_id,
        assumed_weekly_hours: Number(generateForm.assumed_weekly_hours || 5),
        language: generateForm.language || 'en',
        source_notes: generateForm.source_notes,
      });
      openCurriculum(response.data?.curriculum);
      setGenerateNotice({ type: 'success', text: 'Curriculum draft generated. Review it on the right before publishing.' });
      await loadData();
    } catch (generateError) {
      const errorMessage = adminErrorMessage(generateError, 'Could not generate curriculum. Nothing was saved.');
      setGenerateNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const saveCurriculum = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setEditorAction('save');
    setError('');
    setMessage('');
    setEditorNotice({ type: 'working', text: 'Saving curriculum edits...' });
    try {
      const levels = JSON.parse(levelsJson || '[]');
      const response = await adminApi.patch(`/admin/learn/curricula/${selected.id}`, {
        title: selected.title,
        summary: selected.summary,
        total_levels: Number(selected.total_levels || levels.length || 1),
        estimated_total_hours: Number(selected.estimated_total_hours || 1),
        assumed_weekly_hours: Number(selected.assumed_weekly_hours || 5),
        prerequisites: selected.prerequisites || '',
        source_notes: selected.source_notes || '',
        review_notes: selected.review_notes || '',
        language: selected.language || 'en',
        levels,
      });
      setSelected(response.data?.curriculum);
      setLevelsJson(JSON.stringify(response.data?.curriculum?.levels || [], null, 2));
      setEditorNotice({ type: 'success', text: 'Curriculum saved.' });
      await loadData();
    } catch (saveError) {
      const errorMessage = adminErrorMessage(saveError, 'Could not save curriculum.');
      setEditorNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
      setEditorAction('');
    }
  };

  const saveCurriculumWithLevels = async (levels, successMessage = 'Curriculum saved.', options = {}) => {
    if (!selected?.id) return null;
    const { showGlobalMessage = true } = options;
    setSaving(true);
    setError('');
    if (showGlobalMessage) setMessage('');
    try {
      const response = await adminApi.patch(`/admin/learn/curricula/${selected.id}`, {
        title: selected.title,
        summary: selected.summary,
        total_levels: Number(selected.total_levels || levels.length || 1),
        estimated_total_hours: Number(selected.estimated_total_hours || 1),
        assumed_weekly_hours: Number(selected.assumed_weekly_hours || 5),
        prerequisites: selected.prerequisites || '',
        source_notes: selected.source_notes || '',
        review_notes: selected.review_notes || '',
        language: selected.language || 'en',
        levels,
      });
      setSelected(response.data?.curriculum);
      setLevelsJson(JSON.stringify(response.data?.curriculum?.levels || [], null, 2));
      if (showGlobalMessage) setMessage(successMessage);
      await loadData();
      return response.data?.curriculum || null;
    } catch (saveError) {
      const errorMessage = adminErrorMessage(saveError, 'Could not save curriculum.');
      setError(errorMessage);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const getUpdatedLessonLevels = (lessonId, updater) => {
    const levels = parseLevelsJson(levelsJson);
    return levels.map((level) => ({
      ...level,
      lessons: (level.lessons || []).map((lesson) => (lesson.id === lessonId ? updater(lesson) : lesson)),
    }));
  };

  const updateLessonInLevelsJson = (lessonId, updater) => {
    const nextLevels = getUpdatedLessonLevels(lessonId, updater);
    setLevelsJson(JSON.stringify(nextLevels, null, 2));
    return nextLevels;
  };

  const generateLessonDiagram = async (lesson, level) => {
    if (!selected?.id || !lesson?.id) return;
    setDiagramGeneratingId(lesson.id);
    setDiagramStatus(lesson.id, 'working', 'Generating a fresh preview for this lesson...');
    setError('');
    setMessage('');
    try {
      const response = await adminApi.post(`/admin/learn/curricula/${selected.id}/lessons/${lesson.id}/diagram/generate`, {
        level_number: Number(level?.level_number || 0) || undefined,
        level_title: level?.title || '',
        level_objective: level?.objective || '',
        lesson_number: Number(lesson.lesson_number || 0) || undefined,
        lesson_title: lesson.title || '',
        lesson_instruction_md: lesson.instruction_md || '',
        lesson_drill_md: lesson.drill_md || '',
      });
      setDiagramPreviews((prev) => ({
        ...prev,
        [lesson.id]: {
          diagram_svg: response.data?.diagram_svg || '',
          review_notes: response.data?.review_notes || '',
          model_id: response.data?.model_id || '',
          generated_at: response.data?.generated_at || '',
        },
      }));
      setDiagramStatus(lesson.id, 'success', 'Preview ready. Review it here, then choose Use this diagram to save.');
    } catch (diagramError) {
      const errorMessage = adminErrorMessage(diagramError, 'Could not generate a diagram for this lesson.');
      setDiagramStatus(lesson.id, 'error', errorMessage);
      setError(errorMessage);
    } finally {
      setDiagramGeneratingId('');
    }
  };

  const applyDiagramPreview = async (lessonId) => {
    const preview = diagramPreviews[lessonId];
    const safeSvg = safeDiagramSvg(preview?.diagram_svg);
    if (!safeSvg) {
      setDiagramStatus(lessonId, 'error', 'Diagram preview is not a valid safe SVG.');
      return;
    }
    setDiagramStatus(lessonId, 'working', 'Saving this preview to the lesson...');
    const nextLevels = getUpdatedLessonLevels(lessonId, (lesson) => ({
      ...lesson,
      diagram_svg: safeSvg,
      diagram_review_notes: preview.review_notes || '',
      diagram_generated_by_model: preview.model_id || '',
      diagram_generated_at: preview.generated_at || new Date().toISOString(),
    }));
    const saved = await saveCurriculumWithLevels(nextLevels, 'Diagram saved to this lesson.', { showGlobalMessage: false });
    if (saved) {
      setDiagramPreviews((prev) => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
      setDiagramStatus(lessonId, 'success', 'Diagram saved to this lesson. It will show wherever this lesson is visible.');
    } else {
      setDiagramStatus(lessonId, 'error', 'Could not save this diagram. Please try again.');
    }
  };

  const deleteLessonDiagram = async (lessonId) => {
    setDiagramStatus(lessonId, 'working', 'Removing the saved diagram from this lesson...');
    const nextLevels = getUpdatedLessonLevels(lessonId, (lesson) => ({
      ...lesson,
      diagram_svg: null,
      diagram_review_notes: '',
      diagram_generated_by_model: '',
      diagram_generated_at: '',
    }));
    setDiagramPreviews((prev) => {
      const next = { ...prev };
      delete next[lessonId];
      return next;
    });
    const saved = await saveCurriculumWithLevels(nextLevels, 'Diagram removed from this lesson.', { showGlobalMessage: false });
    setDiagramStatus(
      lessonId,
      saved ? 'success' : 'error',
      saved ? 'Diagram removed. This lesson is now text-only.' : 'Could not remove this diagram. Please try again.'
    );
  };

  const publishCurriculum = async () => {
    if (!selected?.id) return;
    if (selected.status === 'published') {
      setEditorNotice({ type: 'success', text: 'This curriculum is already published. Use Save edits to update the live lesson content.' });
      return;
    }
    if (!window.confirm('Publish this curriculum for learners? This makes it visible for enrollment.')) return;
    setSaving(true);
    setEditorAction('publish');
    setError('');
    setMessage('');
    setEditorNotice({ type: 'working', text: 'Publishing curriculum...' });
    try {
      const response = await adminApi.post(`/admin/learn/curricula/${selected.id}/publish`, {
        confirm: true,
        review_notes: selected.review_notes || '',
      });
      setSelected(response.data?.curriculum);
      setEditorNotice({ type: 'success', text: 'Curriculum published.' });
      await loadData();
    } catch (publishError) {
      const errorMessage = publishError.response?.data?.detail || 'Could not publish curriculum.';
      setEditorNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
      setEditorAction('');
    }
  };

  const unpublishCurriculum = async () => {
    if (!selected?.id) return;
    if (!window.confirm('Archive this curriculum? Existing learners keep their copied plan.')) return;
    setSaving(true);
    setEditorAction('archive');
    setError('');
    setMessage('');
    setEditorNotice({ type: 'working', text: 'Archiving curriculum...' });
    try {
      const response = await adminApi.post(`/admin/learn/curricula/${selected.id}/unpublish`);
      setSelected(response.data?.curriculum);
      setEditorNotice({ type: 'success', text: 'Curriculum archived.' });
      await loadData();
    } catch (archiveError) {
      const errorMessage = archiveError.response?.data?.detail || 'Could not archive curriculum.';
      setEditorNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
      setEditorAction('');
    }
  };

  const deleteSelectedCurricula = async (ids = selectedIds, options = {}) => {
    const cleanIds = ids.filter(Boolean);
    if (cleanIds.length === 0) return;
    if (!window.confirm(`Delete ${cleanIds.length} curriculum item(s)? You can undo this from the Deleted view.`)) return;
    const setNotice = options.noticeTarget === 'editor' ? setEditorNotice : setQueueNotice;
    setSaving(true);
    setEditorAction('delete');
    setError('');
    setMessage('');
    setNotice({ type: 'working', text: `Deleting ${cleanIds.length} curriculum item(s)...` });
    try {
      await adminApi.post('/admin/learn/curricula/bulk', { ids: cleanIds, action: 'delete' });
      setSelectedIds([]);
      if (selected?.id && cleanIds.includes(selected.id)) {
        setSelected(null);
        setLevelsJson('[]');
      }
      setNotice({ type: 'success', text: `${cleanIds.length} curriculum item(s) moved to Deleted. Use Deleted view to undo.` });
      await loadData();
    } catch (deleteError) {
      const errorMessage = deleteError.response?.data?.detail || 'Could not delete selected curricula.';
      setNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
      setEditorAction('');
    }
  };

  const restoreSelectedCurricula = async (ids = selectedIds, options = {}) => {
    const cleanIds = ids.filter(Boolean);
    if (cleanIds.length === 0) return;
    const setNotice = options.noticeTarget === 'editor' ? setEditorNotice : setQueueNotice;
    setSaving(true);
    setError('');
    setMessage('');
    setNotice({ type: 'working', text: `Restoring ${cleanIds.length} curriculum item(s)...` });
    try {
      await adminApi.post('/admin/learn/curricula/bulk', { ids: cleanIds, action: 'restore' });
      setSelectedIds([]);
      setNotice({ type: 'success', text: `${cleanIds.length} curriculum item(s) restored.` });
      await loadData();
    } catch (restoreError) {
      const errorMessage = restoreError.response?.data?.detail || 'Could not restore selected curricula.';
      setNotice({ type: 'error', text: errorMessage });
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-lg">
        <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Kalamanch Learn</p>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-950">Curriculum review</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              AI creates drafts. Admin reviews and publishes. Learners only see published curricula.
            </p>
          </div>
          <button type="button" className="btn-outline" onClick={loadData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {error && <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{typeof error === 'string' ? error : error?.message || 'Something went wrong.'}</div>}
        {message && <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div>}
      </section>

      <section className="rounded-3xl border border-purple-100 bg-white p-6 shadow-lg">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-purple-700">Plan configuration</p>
            <h2 className="mt-2 text-2xl font-black text-slate-950">Learn subscription plans</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Configuration only. Creating or activating a plan does not collect payment, create checkout, or place a paywall on Learn.
            </p>
          </div>
          <span className="rounded-full bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-emerald-700">No payment gateway</span>
        </div>
        <div className="mt-5"><ActionNotice notice={planNotice} /></div>

        <form className="mt-5 grid gap-3 rounded-2xl border border-purple-100 bg-purple-50 p-4 md:grid-cols-4" onSubmit={createSubscriptionPlan}>
          <input className="rounded-xl border border-slate-200 px-3 py-2" placeholder="Plan key, e.g. monthly" value={planForm.plan_key} onChange={(event) => setPlanForm((prev) => ({ ...prev, plan_key: event.target.value }))} required />
          <input className="rounded-xl border border-slate-200 px-3 py-2" placeholder="Display name" value={planForm.display_name} onChange={(event) => setPlanForm((prev) => ({ ...prev, display_name: event.target.value }))} required />
          <input className="rounded-xl border border-slate-200 px-3 py-2" type="number" min="0" placeholder="Price INR" value={planForm.price_inr} onChange={(event) => setPlanForm((prev) => ({ ...prev, price_inr: event.target.value }))} />
          <select className="rounded-xl border border-slate-200 px-3 py-2" value={planForm.billing_period} onChange={(event) => setPlanForm((prev) => ({ ...prev, billing_period: event.target.value }))}>
            <option value="free">Free</option><option value="monthly">Monthly</option><option value="annual">Annual</option>
          </select>
          <input className="rounded-xl border border-slate-200 px-3 py-2" type="number" min="0" placeholder="Submissions/month" value={planForm.submissions_per_month} onChange={(event) => setPlanForm((prev) => ({ ...prev, submissions_per_month: event.target.value }))} />
          <label className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-bold"><input type="checkbox" checked={planForm.includes_coached_forms} onChange={(event) => setPlanForm((prev) => ({ ...prev, includes_coached_forms: event.target.checked }))} /> Coached forms</label>
          <label className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-bold"><input type="checkbox" checked={planForm.includes_coach_chat} onChange={(event) => setPlanForm((prev) => ({ ...prev, includes_coach_chat: event.target.checked }))} /> Coach chat</label>
          <button className="rounded-full bg-purple-700 px-5 py-2 font-black text-white disabled:opacity-60" type="submit" disabled={planSaving === 'create'}>{planSaving === 'create' ? 'Creating...' : 'Create inactive plan'}</button>
        </form>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {subscriptionPlans.map((plan) => (
            <article key={plan.plan_key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-black text-slate-950">{plan.plan_key}</p>
                <span className={`rounded-full px-3 py-1 text-xs font-black ${plan.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>{plan.is_active ? 'Active config' : 'Inactive config'}</span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-black text-slate-600">Display name<input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={plan.display_name || ''} onChange={(event) => updatePlanDraft(plan.plan_key, 'display_name', event.target.value)} /></label>
                <label className="text-xs font-black text-slate-600">Price INR<input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" type="number" min="0" value={plan.price_inr ?? 0} disabled={plan.plan_key === 'free'} onChange={(event) => updatePlanDraft(plan.plan_key, 'price_inr', event.target.value)} /></label>
                <label className="text-xs font-black text-slate-600">Billing period<select className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={plan.billing_period || 'free'} disabled={plan.plan_key === 'free'} onChange={(event) => updatePlanDraft(plan.plan_key, 'billing_period', event.target.value)}><option value="free">Free</option><option value="monthly">Monthly</option><option value="annual">Annual</option></select></label>
                <label className="text-xs font-black text-slate-600">Submissions/month<input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" type="number" min="0" value={plan.submissions_per_month ?? 0} onChange={(event) => updatePlanDraft(plan.plan_key, 'submissions_per_month', event.target.value)} /></label>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm font-bold text-slate-700">
                <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(plan.includes_coached_forms)} onChange={(event) => updatePlanDraft(plan.plan_key, 'includes_coached_forms', event.target.checked)} /> Coached forms</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(plan.includes_coach_chat)} onChange={(event) => updatePlanDraft(plan.plan_key, 'includes_coach_chat', event.target.checked)} /> Coach chat</label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className="btn-primary" disabled={planSaving === plan.plan_key} onClick={() => saveSubscriptionPlan(plan)}>Save</button>
                <button type="button" className="btn-outline" disabled={planSaving === plan.plan_key || (plan.plan_key === 'free' && plan.is_active)} onClick={() => setSubscriptionPlanActive(plan, !plan.is_active)}>{plan.is_active ? 'Deactivate' : 'Activate'}</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-lg">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Generate draft</p>
          <form className="mt-5 space-y-4" onSubmit={generateCurriculum}>
            <label className="block text-sm font-black text-slate-900">
              Art form
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={generateForm.art_form_id} onChange={(event) => setGenerateForm((prev) => ({ ...prev, art_form_id: event.target.value }))}>
                {artForms.map((form) => <option key={form.id} value={form.id}>{form.name} · {form.course_tier === 'coached' ? 'Coached Course' : form.course_tier === 'coming_soon' ? 'AI feedback coming soon' : 'Practice Guide'}</option>)}
              </select>
            </label>
            {selectedArtForm && (
              <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                <p className="font-black text-slate-950">{selectedArtForm.course_tier === 'coached' ? 'Coached Course' : selectedArtForm.course_tier === 'coming_soon' ? 'Course · AI feedback coming soon' : 'Practice Guide'}</p>
                <p><b>{selectedArtForm.ground_truth_source}</b> ground truth | {selectedArtForm.input_modality} input</p>
                <p className="mt-2 font-semibold">{selectedArtForm.tier_notice}</p>
              </div>
            )}
            <label className="block text-sm font-black text-slate-900">
              Curriculum language
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={generateForm.language} onChange={(event) => setGenerateForm((prev) => ({ ...prev, language: event.target.value }))}>
                <option value="en">English</option>
                <option value="hi">Hindi</option>
              </select>
            </label>
            <label className="block text-sm font-black text-slate-900">
              Assumed weekly hours
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" max="40" value={generateForm.assumed_weekly_hours} onChange={(event) => setGenerateForm((prev) => ({ ...prev, assumed_weekly_hours: event.target.value }))} />
            </label>
            <label className="block text-sm font-black text-slate-900">
              Source notes from guru/admin
              <textarea className="mt-2 min-h-32 w-full rounded-2xl border border-slate-200 px-4 py-3" value={generateForm.source_notes} onChange={(event) => setGenerateForm((prev) => ({ ...prev, source_notes: event.target.value }))} placeholder="Optional grounding notes. Example: focus on absolute beginners in India." />
            </label>
            <button className="btn-primary w-full" type="submit" disabled={saving || !generateForm.art_form_id}>
              {saving ? 'Working...' : 'Generate curriculum draft'}
            </button>
            <ActionNotice notice={generateNotice} />
          </form>

          <div className="mt-8">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Queue</p>
              <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
                {statusOptions.map((item) => <option key={item || 'all'} value={item}>{item || 'all statuses'}</option>)}
              </select>
            </div>
            {curricula.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl bg-slate-50 p-3">
                <label className="flex items-center gap-2 text-sm font-black text-slate-700">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                  Select all
                </label>
                <span className="text-xs font-bold text-slate-500">{selectedIds.length} selected</span>
                {status === 'deleted' ? (
                  <button type="button" className="rounded-full border border-emerald-200 px-4 py-2 text-xs font-black text-emerald-700" onClick={() => restoreSelectedCurricula()} disabled={saving || selectedIds.length === 0}>
                    Restore selected
                  </button>
                ) : (
                  <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-xs font-black text-red-600" onClick={() => deleteSelectedCurricula()} disabled={saving || selectedIds.length === 0}>
                    Delete selected
                  </button>
                )}
              </div>
            )}
            <div className="mt-3">
              <ActionNotice notice={queueNotice} />
            </div>
            <div className="mt-4 space-y-3">
              {curricula.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-500">No curricula yet.</div>
              ) : curricula.map((curriculum) => (
                <div key={curriculum.id} className={`rounded-2xl border p-4 ${selected?.id === curriculum.id ? 'border-[#FF6F00] bg-orange-50' : 'border-slate-100 bg-slate-50'}`}>
                  <div className="flex items-start gap-3">
                    <input className="mt-1" type="checkbox" checked={selectedIds.includes(curriculum.id)} onChange={() => toggleSelectedId(curriculum.id)} aria-label={`Select ${curriculum.title}`} />
                    <button type="button" onClick={() => openCurriculum(curriculum)} className="min-w-0 flex-1 text-left">
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6F00]">{curriculum.admin_deleted ? 'deleted' : curriculum.status}</p>
                      <h3 className="mt-1 font-black text-slate-950">{curriculum.title}</h3>
                      <p className="mt-1 text-sm font-semibold text-slate-600">{curriculum.art_form_name} | {(curriculum.language || 'en').toUpperCase()} | v{curriculum.version || 1}</p>
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 pl-7">
                    {curriculum.admin_deleted ? (
                      <button type="button" className="rounded-full border border-emerald-200 px-3 py-1.5 text-xs font-black text-emerald-700" onClick={() => restoreSelectedCurricula([curriculum.id])} disabled={saving}>
                        Restore
                      </button>
                    ) : (
                      <button type="button" className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-black text-red-600" onClick={() => deleteSelectedCurricula([curriculum.id])} disabled={saving}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-purple-100 bg-white p-6 shadow-lg">
          {!selected ? (
            <div className="rounded-2xl bg-slate-50 p-6 text-center font-semibold text-slate-500">
              Select or generate a curriculum to review.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Review editor</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">{selected.art_form_name}</h2>
                </div>
                <span className="rounded-full bg-slate-100 px-4 py-2 text-xs font-black uppercase text-slate-700">{selected.status}</span>
              </div>
              <label className="block text-sm font-black text-slate-900">
                Title
                <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={selected.title || ''} onChange={(event) => setSelected((prev) => ({ ...prev, title: event.target.value }))} />
              </label>
              <label className="block text-sm font-black text-slate-900">
                Summary
                <textarea className="mt-2 min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3" value={selected.summary || ''} onChange={(event) => setSelected((prev) => ({ ...prev, summary: event.target.value }))} />
              </label>
              <div className="grid gap-3 md:grid-cols-4">
                <label className="block text-sm font-black text-slate-900">
                  Language
                  <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={selected.language || 'en'} onChange={(event) => setSelected((prev) => ({ ...prev, language: event.target.value }))}>
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                  </select>
                </label>
                <label className="block text-sm font-black text-slate-900">
                  Levels
                  <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" value={selected.total_levels || 1} onChange={(event) => setSelected((prev) => ({ ...prev, total_levels: event.target.value }))} />
                </label>
                <label className="block text-sm font-black text-slate-900">
                  Total hours
                  <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" value={selected.estimated_total_hours || 1} onChange={(event) => setSelected((prev) => ({ ...prev, estimated_total_hours: event.target.value }))} />
                </label>
                <label className="block text-sm font-black text-slate-900">
                  Weekly hours
                  <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" value={selected.assumed_weekly_hours || 5} onChange={(event) => setSelected((prev) => ({ ...prev, assumed_weekly_hours: event.target.value }))} />
                </label>
              </div>
              <section className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Lesson diagram</p>
                <h3 className="mt-2 text-lg font-black text-slate-950">Generate diagrams lesson by lesson</h3>
                <p className="mt-1 text-sm font-semibold text-slate-600">
                  Each Generate button makes one Claude call for that lesson only. Preview first, then choose Use this diagram to save the preview without another model call.
                </p>
                <p className="mt-2 rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-xs font-bold text-emerald-800">
                  Saved Level 1 diagrams show on the public course page. Level 2 onward diagrams show after a learner signs in or enrolls, because those lessons are locked for logged-out visitors.
                </p>
                <div className="mt-4 space-y-4">
                  {parsedLevels.length === 0 ? (
                    <div className="rounded-2xl bg-white p-4 text-sm font-bold text-slate-500">
                      No lessons found. Check the levels JSON first.
                    </div>
                  ) : parsedLevels.map((level) => (
                    <div key={level.id || level.level_number} className="rounded-2xl border border-emerald-100 bg-white p-4">
                      <h4 className="text-sm font-black text-slate-950">Level {level.level_number || ''}: {level.title || 'Untitled level'}</h4>
                      <div className="mt-3 space-y-3">
                        {(level.lessons || []).map((lesson) => {
                          const preview = diagramPreviews[lesson.id];
                          const lessonStatus = diagramStatuses[lesson.id];
                          const hasSavedDiagram = Boolean(safeDiagramSvg(lesson.diagram_svg));
                          const isGenerating = diagramGeneratingId === lesson.id;
                          const isLessonSaving = saving && lessonStatus?.type === 'working';
                          return (
                            <div key={lesson.id || `${level.level_number}-${lesson.lesson_number}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-black text-slate-950">Lesson {lesson.lesson_number || ''}: {lesson.title || 'Untitled lesson'}</p>
                                  <p className="mt-1 text-xs font-bold text-slate-500">{hasSavedDiagram ? 'Diagram currently attached' : 'Text-only lesson'}</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-black text-white disabled:opacity-60"
                                    onClick={() => generateLessonDiagram(lesson, level)}
                                    disabled={saving || isGenerating || !lesson.id}
                                  >
                                    {isGenerating ? 'Generating...' : preview ? 'Regenerate' : 'Generate diagram'}
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded-full border border-red-200 px-4 py-2 text-xs font-black text-red-600 disabled:opacity-50"
                                    onClick={() => deleteLessonDiagram(lesson.id)}
                                    disabled={saving || (!hasSavedDiagram && !preview)}
                                  >
                                    {isLessonSaving ? 'Working...' : 'Delete diagram'}
                                  </button>
                                </div>
                              </div>
                              {lessonStatus && (
                                <div
                                  className={[
                                    'mt-3 rounded-2xl px-4 py-3 text-xs font-black',
                                    lessonStatus.type === 'error'
                                      ? 'border border-red-200 bg-red-50 text-red-700'
                                      : lessonStatus.type === 'working'
                                        ? 'border border-amber-200 bg-amber-50 text-amber-800'
                                        : 'border border-emerald-200 bg-emerald-50 text-emerald-800',
                                  ].join(' ')}
                                >
                                  {lessonStatus.text}
                                </div>
                              )}
                              {hasSavedDiagram && (
                                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
                                  <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">Current saved draft diagram</p>
                                  <DiagramPreview svg={lesson.diagram_svg} />
                                </div>
                              )}
                              {preview && (
                                <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">New preview, not saved</p>
                                      {preview.review_notes && <p className="mt-1 text-xs font-bold text-emerald-900">{preview.review_notes}</p>}
                                    </div>
                                    <button
                                      type="button"
                                      className="rounded-full bg-[#FF6F00] px-4 py-2 text-xs font-black text-white disabled:opacity-60"
                                      onClick={() => applyDiagramPreview(lesson.id)}
                                      disabled={saving}
                                    >
                                      {isLessonSaving ? 'Saving...' : 'Use this diagram'}
                                    </button>
                                  </div>
                                  <DiagramPreview svg={preview.diagram_svg} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              <label className="block text-sm font-black text-slate-900">
                Levels and lessons JSON
                <textarea className="mt-2 min-h-[28rem] w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono text-xs" value={levelsJson} onChange={(event) => setLevelsJson(event.target.value)} />
              </label>
              <label className="block text-sm font-black text-slate-900">
                Review notes
                <textarea className="mt-2 min-h-20 w-full rounded-2xl border border-slate-200 px-4 py-3" value={selected.review_notes || ''} onChange={(event) => setSelected((prev) => ({ ...prev, review_notes: event.target.value }))} />
              </label>
              <div className="flex flex-wrap gap-3">
                <button type="button" className="btn-outline disabled:cursor-not-allowed disabled:opacity-60" onClick={saveCurriculum} disabled={saving}>
                  {editorAction === 'save' ? 'Saving...' : 'Save edits'}
                </button>
                <button
                  type="button"
                  className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={publishCurriculum}
                  disabled={saving || selected.status === 'published'}
                  title={selected.status === 'published' ? 'Already published. Save edits updates the live curriculum.' : 'Publish this curriculum'}
                >
                  {selected.status === 'published' ? 'Published' : editorAction === 'publish' ? 'Publishing...' : 'Publish'}
                </button>
                <button
                  type="button"
                  className="rounded-full border border-red-200 px-5 py-3 text-sm font-black text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={unpublishCurriculum}
                  disabled={saving || selected.status === 'archived'}
                >
                  {editorAction === 'archive' ? 'Archiving...' : 'Archive'}
                </button>
                {selected.admin_deleted ? (
                  <button type="button" className="rounded-full border border-emerald-200 px-5 py-3 text-sm font-black text-emerald-700" onClick={() => restoreSelectedCurricula([selected.id])} disabled={saving}>Restore</button>
                ) : (
                  <button type="button" className="rounded-full border border-red-200 px-5 py-3 text-sm font-black text-red-600 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => deleteSelectedCurricula([selected.id], { noticeTarget: 'editor' })} disabled={saving}>
                    {editorAction === 'delete' ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
              <ActionNotice notice={editorNotice} />
              {selected.status === 'published' && (
                <p className="text-xs font-bold text-slate-500">
                  This curriculum is already live. Use Save edits after saving diagrams or text changes; Publish is only needed for drafts.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
